/**
 * Utils for managing tag structure in a text editing context
 */

import _ from 'lodash';
import $ from 'jquery';
import {reportBug} from '@packages/utilities/reporter';
import {pair, getFirst, getSecond, Pair} from '@packages/utilities/Pair';
import * as StringUtils from '@packages/utilities/string-utils';
import {NORMAL_NODE, TEXT_NODE} from '@packages/shared/utilities/DomUtils';
import {
  RTE_FIGURE_TYPE_ATTR,
  RTE_FIGURE_ALIGN_ATTR,
  RTE_FIGURE_MAX_WIDTH_ATTR,
  RTE_FIGURE_MAX_HEIGHT_ATTR,
  RTE_FIGURE_DIMENSIONS_ATTR,
  RTE_LINK_TYPE_ATTR,
  RTE_LINK_PAGE_ID_ATTR,
  RTE_LINK_ITEM_ID_ATTR,
  RTE_LINK_COLLECTION_ID_ATTR,
  RTE_LINK_DOM_ID_ATTR,
  RTE_FIGURE_PAGE_URL_ATTR,
  RTE_EMBED_TYPE_ATTR,
} from '@packages/systems/core/constants/SharedConfig';
import {frontEndRteHtmlToJsonNodes} from '@packages/systems/core/utils/JsonDomUtils';
import {reifyElementTree} from '@packages/systems/core/render';
import {isRichText} from '@packages/utilities/elementTypeUtils';
import {Plugins, Component} from '@packages/systems/wfdl';
import {ExpressionElement, EText} from '@packages/systems/wfdl-ast';
import {ContentEditorActive} from '@packages/shared/states';
import {isXscpData} from '@packages/systems/core/records/Xscp';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {trackEvent} from '@packages/utilities/analytics';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import DOMPurify from 'dompurify';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {removeNonNumberChars} from '@packages/systems/core/utils/NumberUtils';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {incrementGuid} from '@packages/utilities/guid';
import {Dispatch} from '@packages/systems/flux';
import {INVALID_PASTE_REJECTED} from '@packages/systems/designer/actions';
import {
  getAllTextInElement,
  instantiateElement,
  isNumberEditing,
  needsContentEditorPlaintextMode,
  patchLineBreakElements,
} from '@packages/systems/ContentEditor';
import {filterElementTree, isUserComponent} from '@packages/utilities/platform';
import {insertPastedNodes} from './ContentEditorStoreUtils';
import {contentEditorPasteComponentsStripped} from './actions';

const MAX_RECURSION_DEPTH = 100;
const BODY_OPEN = '<body>';
const BODY_CLOSE = '</body>';
const RICH_TEXT_NODES = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'figure',
  'figcaption',
  'a',
  'strong',
  'em',
  'sup',
  'sub',
  'code',
  'div',
  'img',
  'iframe',
  'ul',
  'ol',
  'li',
  'br',
]);

const NODES_ONLY_WITHIN_HTML_EMBED = new Set([
  'script',
  'style',
  'link',
  // Table elements
  'table',
  'caption',
  'thead',
  'tr',
  'th',
  'td',
  'tbody',
  'tfoot',
  // layout elements
  'hr',
  'aside',
  'span',
  'pre',
  'i',
  'header',
  // Form elements
  'form',
  'input',
  'textarea',
  'select',
  'option',
  'optgroup',
  'datalist',
  'button',
  'label',
  'fieldset',
  'legend',
  // Disclosure elements
  'details',
  'summary',
]);

const HTML_EMBED_NODES = new Set([
  ...RICH_TEXT_NODES,
  ...NODES_ONLY_WITHIN_HTML_EMBED,
]);

const VALID_ATTRIBUTES = new Set([
  RTE_FIGURE_TYPE_ATTR,
  RTE_FIGURE_ALIGN_ATTR,
  RTE_FIGURE_MAX_WIDTH_ATTR,
  RTE_FIGURE_MAX_HEIGHT_ATTR,
  RTE_FIGURE_DIMENSIONS_ATTR,
  RTE_FIGURE_PAGE_URL_ATTR,
  'src',
  'target',
  'href',
  'href-disabled',
  'href-disabled-default-color',
  'href-disabled-underline',
  'alt',
  'frameborder',
  'scrolling',
  'allowfullscreen',
  'start',
  RTE_LINK_DOM_ID_ATTR,
  RTE_LINK_PAGE_ID_ATTR,
  RTE_LINK_ITEM_ID_ATTR,
  RTE_LINK_COLLECTION_ID_ATTR,
  RTE_LINK_TYPE_ATTR,
  RTE_EMBED_TYPE_ATTR,
]);
const VALID_ATTRIBUTES_ONLY_WITHIN_HTML_EMBED = new Set([
  'class',
  'style',
  // iframe attributes
  'width',
  'height',
  'loading',
  'referrerpolicy',
  // aria attributes
  'role',
  'aria-colcount',
  'aria-rowcount',
  'aria-label',
  'aria-sort',
  // table attributes
  'scope',
  'colspan',
  // link attributes
  'rel',
  'media',
  'hreflang',
  // other attributes
  'title',
  'type',
]);
const VALID_ATTRIBUTES_WITHIN_HTML_EMBED = new Set([
  ...VALID_ATTRIBUTES,
  ...VALID_ATTRIBUTES_ONLY_WITHIN_HTML_EMBED,
]);
const NO_PRESERVATION_NODES = new Set(['head', 'title', 'style', 'script']); // NORMAL_NODEs that may contain descendant text_nodes that we don't want to perserve

const COMPONENT_WHTML_NODES = new Set(['wf-component', 'wf-prop']);
const NORMAL_FONT_WEIGHT = 400;
const HEADING_REGEX = /h\d/;
const HTTP_PROTO_REGEX = /^http:/;

const mutInstantiateReducer = (
  acc: {els: Array<ExpressionElement>; nextId: string},
  el: ExpressionElement
) => {
  const [{id}, newEl] = instantiateElement(el, acc.nextId);
  acc.els.push(newEl);
  acc.nextId = id;
  return acc;
};

// Create a single string element with the numerical text from the elements provided
const createNumericalOnlyStringElement = ([elements, nextId]: Pair<
  Array<ExpressionElement>,
  string
>): Pair<Array<ExpressionElement>, string> => {
  // Get all text from the pasted elements
  const allText = elements
    .map((element) => getAllTextInElement(element))
    .join('');

  const numericalText = removeNonNumberChars(allText);

  const stringElement = {
    id: nextId,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText(numericalText),
  };

  return [[stringElement], incrementGuid(nextId)];
};

export type ContentEditorClipboardEvent = {
  clipboardData: Pick<DataTransfer, 'getData' | 'types'>;
};

export function handlePaste(
  seedId: string,
  event: ContentEditorClipboardEvent,
  state: ContentEditorActive,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  dispatch: Dispatch,
  isEditingSelectedNodeInInstance: boolean = false,
  isMultilineField: boolean = false,
  shouldStripComponents: boolean = false
): ContentEditorActive {
  const {subtree} = state;
  let elements: Array<ExpressionElement> = [];
  let nextId: string = seedId;
  let rawClipboardData;
  let cleanedClipboardData;
  const jsonData = event.clipboardData.getData('application/json');
  const needsPlaintext = needsContentEditorPlaintextMode(state);
  const clipboardContentType = needsPlaintext ? 'simple' : 'rich';

  // paste into rich text
  if (jsonData && !needsPlaintext) {
    try {
      const clipboardJson = JSON.parse(jsonData);
      // checks if the user is attempting to paste a whole element after selecting it from outside the ContentEditor.
      if (isXscpData(clipboardJson)) {
        dispatch({
          type: INVALID_PASTE_REJECTED,
        });
        return state;
      }
      if (Array.isArray(clipboardJson)) {
        const result = reifyElementTree(
          clipboardJson.slice(1),
          components,
          plugins
        ).reduce<{els: Array<ExpressionElement>; nextId: string}>(
          mutInstantiateReducer,
          {els: [], nextId}
        );
        elements = result.els;
        nextId = result.nextId;
      }
    } catch (err: any) {
      console.error('Could not parse Content Editor clipboard json data', err);
    }
  }
  // TODO(CMSAUTH-2469): Pasting Markdown needs to be implemented
  else {
    [rawClipboardData, cleanedClipboardData] = cleanClipboardContent(
      event,
      clipboardContentType
    );
    const {jsonContentNodes, nextId: idFromJsonNodes} =
      frontEndRteHtmlToJsonNodes(
        // if we are updating an override field via content editor and the field is not multiline,
        // replace the line breaks with spaces
        cleanedClipboardData &&
          isEditingSelectedNodeInInstance &&
          !isMultilineField
          ? cleanedClipboardData.replace(/\r|\n/g, ' ')
          : cleanedClipboardData,
        {supportHtmlEmbeds: true, seedId: nextId}
      ) as {
        jsonContentNodes: JSONNode[];
        rootChildIds: string[];
        nextId: string;
      };

    const result = patchLineBreakElements(
      pair(
        reifyElementTree(jsonContentNodes, components, plugins),
        idFromJsonNodes
      )
    );
    // if we are editing numbers we just want to paste the number characters within a string element
    if (isNumberEditing(state)) {
      const res = createNumericalOnlyStringElement(result);
      elements = getFirst(res);
      nextId = getSecond(res);
    } else {
      elements = getFirst(result);
      nextId = getSecond(result);
    }
  }

  if (shouldStripComponents) {
    const originalLength = elements.length;

    elements = elements
      .filter((el) => !isUserComponent(el.type))
      .map((el) =>
        filterElementTree(el, (child) => !isUserComponent(child.type))
      );

    if (elements.length === 0 && originalLength > 0) {
      dispatch(contentEditorPasteComponentsStripped());
      return state;
    }
  }

  const isRichTextCopyPasteAnalyticsEnabled = getFeatureFlagOutsideOfReact(
    FEATURE_FLAGS.RICH_TEXT_COPY_PASTE_ANALYTICS
  );
  if (isRichText(subtree) && isRichTextCopyPasteAnalyticsEnabled) {
    const {collectionId, fromModal} = state;
    const isSanitized = !(jsonData && !needsPlaintext);
    let source;
    if (collectionId === null) {
      source = fromModal ? 'modal' : 'data manager';
    } else {
      source = 'designer canvas';
    }

    trackEvent({
      name: ANALYTICS_EVENTS.RICH_TEXT_CONTENT_PASTED,
      data: {
        'is sanitized': isSanitized,
        'sanitized data': {
          raw: rawClipboardData,
          clean: cleanedClipboardData,
        },
        source,
      },
    });
  }

  return getFirst(insertPastedNodes(elements)(pair(state, nextId)));
}

/**
 * Clean clipboard content
 * @param {Event} event the paste event
 * @param {String} type 'rich', 'simple'
 * @return {Array} The raw and cleaned content
 */
function cleanClipboardContent(
  event: any,
  type: 'rich' | 'simple'
): Array<string | null | undefined> {
  if (!event.clipboardData) return [];

  const clipboardData = event.clipboardData;

  if (clipboardData.types.indexOf('text/html') > -1) {
    const clipboardHtml = clipboardData.getData('text/html');
    const cleanedHtml = cleanClipboardHtml(type, clipboardHtml);
    return [clipboardHtml, cleanedHtml];
  } else if (clipboardData.types.indexOf('text/plain') > -1) {
    const clipboardPlaintext = clipboardData.getData('text/plain');
    const cleanedPlaintext = cleanClipboardPlaintext(type, clipboardPlaintext);
    return [clipboardPlaintext, cleanedPlaintext];
  } else {
    return ['', ''];
  }
}

/**
 * clean clipboard html - clears away bad tags, inline styles, and more
 * @param  {string} type          rich or simple - the type of node to clean for
 * @param  {string} clipboardHtml html - typically taken from the clipboard
 * @return {string}               the cleaned html
 */
export function cleanClipboardHtml(
  type: 'rich' | 'simple',
  clipboardHtml: string,
  supportHtmlEmbeds: boolean = false,
  preserveComponents: boolean = false
): string {
  // When cleaning text/plain we would treat newlines as paragraph separators,
  // but when cleaning text/html we assume paragraphs would be html <p>s and
  // treat unicode newlines as line breaks
  // We also need to check for &nbsp; unicode chars (\u00a0/g) and convert
  // them to regular spaces.
  const rinsedClipboardHtml = clipboardHtml
    .replace(/\u00a0/g, ' ')
    .replace(/<pre(.|\s)*?>(.|\s)*?<\/pre(.|\s)*?>/g, (pre) =>
      pre
        .replace(/<pre(.|\s)*?>/, (openTag) =>
          openTag
            // remove newlines from the open tag
            .replace(/\r|\n/g, '')
        )
        .replace(/<\/pre(.|\s)*?>/, (closeTag) =>
          closeTag
            // remove newlines from the closeTag tag
            .replace(/\r|\n/g, '')
        )
        // convert remaining newlines inside the pre element to line breaks
        .replace(/\r|\n/g, '<br>')
    )
    // remove newlines from everywhere else.
    .replace(/\r|\n/g, '');

  // Todo: When tagEnforcement is implemented for simple nodes make this a constant
  if (type === 'simple') {
    // validTags = SIMPLE_TEXT_NODES;
    // Todo: implement simple text tag enforcement
    return cleanPasted(rinsedClipboardHtml);
  }

  const getValidTags = (isInHtmlEmbed: boolean) => {
    if (type === 'rich') {
      if (isInHtmlEmbed) {
        return HTML_EMBED_NODES;
      }
      return RICH_TEXT_NODES;
    }
  };

  // Use jquery to setup a manipulatable node
  const $pocket = $('<div></div>');
  $pocket.html(rinsedClipboardHtml);

  // If we have a <body> tag inside the HTML, we only want to paste its html
  const $body = $pocket.find('body');
  if ($body.length) {
    $pocket.html($body.html());
  }

  // We can't process non-standard nodes (things like html comments and doctypes) the same as standard nodes
  // so store references to them in an array and remove them after the processDescendants() traversal
  const nonStandardNodes: Array<Element | any> = [];

  const restrict = function restrict(
    child: HTMLElement,
    depth: number,
    isInHtmlEmbed: boolean = false
  ) {
    if (child.nodeType === NORMAL_NODE) {
      const newChild = isInHtmlEmbed
        ? child
        : convertAlternateNode(type, child);

      if (newChild && newChild !== child) {
        child = newChild;
        processDescendants(child, restrict, depth, {
          element: $pocket.html(),
          func: 'cleanClipboardHtml2',
          supportHtmlEmbeds,
          isInHtmlEmbed,
        });
      }

      const validTags = getValidTags(isInHtmlEmbed);

      const nodeName = child.nodeName.toLowerCase();
      const $child = $(child);
      // Try and calm the chaos

      const isPreservedComponentTag =
        preserveComponents && COMPONENT_WHTML_NODES.has(nodeName);

      // make sure it's a valid tag, structure is enforced elsewhere
      if (NO_PRESERVATION_NODES.has(nodeName) && !isInHtmlEmbed) {
        // Nodes like a style node can contain a text_node that we don't want to 'rescue'
        // So treat them as a non-standard node
        nonStandardNodes.push(child);
      } else if (
        validTags &&
        !validTags.has(nodeName) &&
        !isPreservedComponentTag &&
        !$child.is('[data-w-rte-paste-marker]')
      ) {
        // If it's invalid queue the node for deletion
        // @ts-expect-error - TS2769 - No overload matches this call
        $child.attr('data-w-rte-clean-remove', true);
      } else if (isPreservedComponentTag) {
        // no-op: keep the component tag verbatim
      } else {
        // If we're keeping it, clean it up
        // Only keep allowlisted attributes
        const attrs = _.map(child.attributes, 'name');
        _.each(attrs, (attr) => {
          let listOfValidAttributes = VALID_ATTRIBUTES;
          if (supportHtmlEmbeds && isInHtmlEmbed) {
            // If we're in an html embed, we allow for some additional
            // attributes that aren't normally allowed outside of
            // html embeds
            listOfValidAttributes = VALID_ATTRIBUTES_WITHIN_HTML_EMBED;
          }
          if (!listOfValidAttributes.has(attr)) {
            child.removeAttribute(attr);
          }
        });
      }
    } else if (child.nodeType !== TEXT_NODE) {
      const isHtmlEmbedComment =
        isInHtmlEmbed && child.nodeType === Node.COMMENT_NODE;

      // Allow HTML comments inside HTML embeds. All other non-text nodes are
      // treated as non-standard and removed.
      if (!isHtmlEmbedComment) {
        nonStandardNodes.push(child);
      }
    }
  };

  // Restrict html to allowed nodes, this will strip out <meta> and comments and so on
  //@ts-expect-error - TS2345 - Argument of type 'HTMLElement | undefined' is not assignable to parameter of type 'Element'.
  processDescendants($pocket[0], restrict, 0, {
    element: $pocket.html(),
    func: 'cleanClipboardHtml',
    supportHtmlEmbeds,
    isInHtmlEmbed: false,
  });

  // Remove the non-standard nodes
  for (let i = 0; i < nonStandardNodes.length; i++) {
    const node = nonStandardNodes[i];
    node.parentNode && node.parentNode.removeChild(node);
  }

  // Process standard nodes, this will strip out html tags that we don't allow in rich text
  // preserving their children as surviving siblings
  // Now lets actually delete some unwanted nodes
  $pocket.find('[data-w-rte-clean-remove]').each(function (index, el) {
    // Unpack this node's children behind it.
    const parentNode = el.parentNode;
    const _children = el.childNodes;
    while (_children.length) {
      // @ts-expect-error - TS2345 - Argument of type 'ChildNode | undefined' is not assignable to parameter of type 'Node'.
      parentNode?.insertBefore(_children[0], el);
    }
    parentNode?.removeChild(el);
  });

  $pocket.find('iframe').each(function (index, el) {
    // make sure pasted in video embed links are https, or they won't load in the CMS/designer and will cause chaos
    const $el = $(el);
    const src = $el.attr('src');
    if (src && src.match(HTTP_PROTO_REGEX)) {
      $el.attr('src', src.replace(HTTP_PROTO_REGEX, 'https:'));
    }
  });

  // Extract <img> and <iframe> elements from inline formatting wrappers
  // (e.g. <strong><img ...></strong> or <em><strong><img></strong></em>
  // from WordPress exports). Figure-content elements nested inside inline
  // formatting elements cause infinite recursion in enforceTagStructure's
  // raiseElementToAbsorbingParent. Loop handles arbitrary nesting depth.
  const INLINE_FORMAT_TAGS = new Set(['strong', 'em', 'sup', 'sub', 'span']);
  $pocket.find('img, iframe').each(function (_index, el) {
    const $el = $(el);
    let $parent = $el.parent();
    while (
      $parent.length &&
      INLINE_FORMAT_TAGS.has($parent.prop('nodeName').toLowerCase())
    ) {
      $el.insertBefore($parent);

      if (!$parent.contents().length) {
        $parent.remove();
      }

      $parent = $el.parent();
    }
  });

  // Content will be returned as a string
  return _.trim($pocket.html(), ' \n\t\r\f');
}

/**
 * clean clipboard plaintext
 * @param {String} mode The RTE mode to clean plaintext for
 * @param {String} clipboardPlaintext Plaintext from the clipboard
 * @return {String} Cleaned clipboard content
 */
export function cleanClipboardPlaintext(
  mode: 'rich' | 'simple',
  clipboardPlaintext: string
): string {
  // Note: copying html from a plaintext source (like a text editor)
  // should be pasted as plaintext
  if (mode === 'rich') {
    // cleanup
    clipboardPlaintext = _.trim(clipboardPlaintext, ' \n\t\r\f');
    // replace angle brackets with escaped tags
    clipboardPlaintext = clipboardPlaintext.replace(/&/g, '&amp;');
    clipboardPlaintext = clipboardPlaintext.replace(/</g, '&lt;');
    clipboardPlaintext = clipboardPlaintext.replace(/>/g, '&gt;');
    // pickup double line newline characters and treat them as new paragraphs
    clipboardPlaintext =
      '<p>' + clipboardPlaintext.replace(/[\r\n][\r\n]/g, '</p><p>') + '</p>';
    // convert any remaining single newline characters to line breaks
    clipboardPlaintext = clipboardPlaintext.replace(/\r|\n/g, '<br>');
  } else if (mode === 'simple') {
    clipboardPlaintext = cleanPasted(clipboardPlaintext);
  }
  return clipboardPlaintext;
}

/**
 * Converts pasted HTML to a text string.
 * @param  {String} html The original HTML from the clipboard.
 * @return {String}      The text version of the same HTML - with only breaks preserved.
 */
function cleanPasted(html: string): string {
  if (!html) {
    return '';
  }
  if (_.isObject(html)) {
    return 'Unknown Text';
  }
  if (!_.isString(html)) {
    return String(html);
  }

  html = html.replace("<meta charset='utf-8'>", '');

  // If we have a <body> tag inside the HTML, grab just its contents since the <head> could have
  // some pastable content (e.g. CSS in the <style> tag)
  if (html.indexOf(BODY_OPEN) > 0 && html.indexOf(BODY_CLOSE) > 0) {
    html = StringUtils.strLeft(
      StringUtils.strRight(html, BODY_OPEN),
      BODY_CLOSE
    );
  }

  // Remove inline style attributes, HTML comments, and font tags
  html = html.replace(/\s*style="[^"]*"\s*/g, '');
  html = html.replace(/<!--[\s\S]*?-->/g, '');
  html = removeFontTags(html);

  // Replace break tags with a special placeholder
  html = html.replace(/<br>|\r/g, '[-BREAK-]');

  // Protect against XSS vulnerabilities
  // Should be called before $() call with user provided value to prevent XSS. Read more: https://api.jquery.com/html/ (search for XSS)
  html = DOMPurify.sanitize(html);

  // Create a temp node just to get jQuery to help us convert the contents to text
  const tempNode = $('<div>' + html + '</div>');

  // Remove any embedded <style> blocks
  tempNode.find('style').remove();

  // Convert to text using jQuery magic
  const newHtml = (() => {
    let _newHtml = tempNode.text();

    // Add line breaks back in since we want to keep them
    _newHtml = _newHtml.replace(/\[-BREAK-\]/g, '<br>');

    // Remove edge line breaks
    _newHtml = StringUtils.trimWord(_newHtml, '<br>');

    // Cleanup
    return _.trim(_newHtml, ' \n\t\r\f');
  })();

  // Remove the temporary node
  tempNode.remove();

  return newHtml;
}

/**
 * Convert alternate nodes and styles, eg: <b> or <span style="font-weight:bolder"> to <strong>
 * @param  {String} type 'rich' or 'simple'
 * @param  {DomNode} node the node to analyse
 * @return {DomNode} if converted to a new node, will return that new node for processing
 */
function convertAlternateNode(
  type: 'rich' | 'simple',
  node: HTMLElement
): HTMLElement {
  const $node: JQuery<HTMLElement> = $(node);
  const nodeName = node.nodeName.toLowerCase();
  // NOTE: Some copy sources like Google Docs use <b style="font-weight:normal"> for normal text
  const fontWeight = $node.css('font-weight');
  const fontIsNaN = isNaN(parseInt(fontWeight, 10));
  const isBoldStyle = (() => {
    const hasBoldStyle =
      (fontIsNaN && (fontWeight === 'bold' || fontWeight === 'bolder')) ||
      // @ts-expect-error - TS2365 - Operator '>' cannot be applied to types 'string' and 'number'.
      (!fontIsNaN && fontWeight > NORMAL_FONT_WEIGHT);
    // Headings will most likely have 'bold' styles in the clipboard when copied from another site, and in the designer style manager
    // so better to prefer not add <strong> tags here
    if (hasBoldStyle && (HEADING_REGEX.test(nodeName) || nodeName === 'li')) {
      return false;
    } else {
      return hasBoldStyle;
    }
  })();

  const isBoldTag = nodeName === 'b' && (fontWeight === '' || isBoldStyle);
  const isStrongBoldTag =
    (nodeName === 'strong' && (fontWeight === '' || isBoldStyle)) || isBoldTag;

  const fontStyle = $node.css('font-style');
  const isEmTag = nodeName === 'em';
  // Note: try to differentiate between <i>s used properly (as italic) and <i>s used improperly (as icons)
  const isITag = nodeName === 'i' && _.trim($node.html()) !== '';
  const isItalicStyle = fontStyle === 'italic' || fontStyle === 'oblique';

  const verticalAlign = $node.css('vertical-align');
  const isSupTag = verticalAlign === 'super' || nodeName === 'sup';

  if (isSupTag) {
    // @ts-expect-error - TS2322 - Type 'HTMLElement | undefined' is not assignable to type 'Element'
    return convertElement(node, 'sup')[0];
  }

  const isSubTag = verticalAlign === 'sub' || nodeName === 'sub';

  if (isSubTag) {
    // @ts-expect-error - TS2322 - Type 'HTMLElement | undefined' is not assignable to type 'Element'
    return convertElement(node, 'sub')[0];
  }

  if (isBoldTag) {
    // If it's a <b> tag, convert it to a <strong> tag
    // @ts-expect-error - TS2322 - Type 'HTMLElement | undefined' is not assignable to type 'Element'
    return convertElement(node, 'strong')[0];
  } else if (!isStrongBoldTag && isBoldStyle) {
    // If it's not a <strong> or <b> tag but it has bold style
    // nest a <strong> tag inside and move all it's children inside
    $node.css('font-weight', '');
    $('<strong></strong>').append($node.contents()).appendTo($node);
  } else {
    $node.css('font-weight', '');
  }

  if (isITag) {
    // If it's a <i> tag used for italic text, convert it to a <em> tag
    // @ts-expect-error - TS2322 - Type 'HTMLElement | undefined' is not assignable to type 'Element'
    return convertElement(node, 'em')[0];
  } else if (!isEmTag && isItalicStyle) {
    $node.css('font-style', '');

    $('<em></em>').append($node.contents()).appendTo($node);
  } else {
    $node.css('font-style', '');
  }

  // clean up empty inline style attributes
  if ($node.attr('style') === '') {
    $node.removeAttr('style');
  }
  return node;
}

/**
 * Removes empty <font> tags from a block of HTML. Case insensitive (includes <font> and <FONT> tags).
 * @param  {String} html The original HTML.
 * @return {String}      The cleaned HTML, with all empty <font> tags removed.
 */
function removeFontTags(html: string): string {
  if (!html) {
    return html;
  }

  // Replace opening and closing tags in one go
  html = html.replace(/<\/?font ?[^>]*>/gi, '');

  return html;
}

/**
 * convert an element to another
 * @param  {domNode} element The element to convert
 * @param  {string} tag     The tag name to convert to
 * @return {jQuery}         a the converted node, jQuery wrapped
 */
export function convertElement(element: Element, tag: string) {
  const attributes: Record<string, any> = {};

  _.each(element.attributes, function (attr) {
    attributes[attr.name] = attr.value;
  });

  const newEl = $('<' + tag + ' />', attributes).append($(element).contents());

  $(element).replaceWith(function () {
    return newEl;
  });

  return newEl;
}

export function processDescendants(
  parent: Element,
  cb: (child: HTMLElement, depth: number, isInHtmlEmbed: boolean) => void,
  depth: number,
  metadataInput: Record<any, any>
) {
  const supportHtmlEmbeds = Boolean(metadataInput.supportHtmlEmbeds);
  const parentHasHtmlEmbedTypeAttribute =
    supportHtmlEmbeds &&
    Boolean(
      // sometimes we're passed in a string rather than an element here
      'getAttribute' in parent && parent.getAttribute(RTE_EMBED_TYPE_ATTR)
    );
  const isCurrentlyInHtmlEmbed =
    supportHtmlEmbeds && Boolean(metadataInput.isInHtmlEmbed);
  const isInHtmlEmbed =
    isCurrentlyInHtmlEmbed || parentHasHtmlEmbedTypeAttribute;

  let metadata = metadataInput;
  if (supportHtmlEmbeds && metadata.isInHtmlEmbed !== isInHtmlEmbed) {
    metadata = {...metadata, isInHtmlEmbed};
  }

  const children = parent.childNodes;

  if (!depth) depth = 0;
  depth += 1;

  // Note: monitor depth, if a situation where new nodes
  // are created or modified in a way where this function is called
  // way to many times approaching the infinite we should let bug report
  // know about it.
  if (depth >= MAX_RECURSION_DEPTH) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('RTERecursion', 'nesting level at ' + depth, {
      metaData: metadata,
    });
    throw new Error('RTE Recursion depth limit');
  }

  for (let i = 0; i < children.length; i++) {
    // @ts-expect-error - TS2740 - Type 'ChildNode' is missing the following properties from type 'Element': attributes, classList, className, clientHeight, and 110 more.
    const child: HTMLElement = children[i];
    cb(child, depth, supportHtmlEmbeds && isInHtmlEmbed);
    processDescendants(child, cb, depth, metadata);
  }
}

export const forTestSuite = {
  VALID_ATTRIBUTES: Array.from(VALID_ATTRIBUTES),
  VALID_ATTRIBUTES_WITHIN_HTML_EMBED: Array.from(
    VALID_ATTRIBUTES_WITHIN_HTML_EMBED
  ),
  VALID_ATTRIBUTES_ONLY_WITHIN_HTML_EMBED: Array.from(
    VALID_ATTRIBUTES_ONLY_WITHIN_HTML_EMBED
  ),
  RICH_TEXT_NODES: Array.from(RICH_TEXT_NODES),
  NO_PRESERVATION_NODES: Array.from(NO_PRESERVATION_NODES),
} as const;
